/**
 * @voltxampere/web — Explosion preferences (Sprint 35-5)
 *
 * AYARLAR > Patlama Efektleri sub-section toggles for the Sprint 35
 * LED + capacitor + resistor explosion animations + the smoke
 * particle pool. Bridges two signals to the IDB settings store via
 * `SettingsRecord.explosion` additive field — no DB version bump.
 *
 * Pattern mirrors `live-mode-prefs.ts` (Sprint 33-4) +
 * `thermal-prefs.ts` (Sprint 34-5).
 *
 * Defaults: both true. Hydrate uses `?? true` so v4/v5 settings
 * records (no explosion field) start with both enabled.
 *
 * 3-katman reduced-motion guard (per ADR-0057):
 *   1. AYARLAR `enabled` toggle (this module)
 *   2. `prefers-reduced-motion: reduce` (apps/web canvas effect)
 *   3. AYARLAR `smokeEnabled` separate (perf-conscious)
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

/** Master toggle for all explosion animations (LED + cap + resistor). */
export const explosionEnabledSignal = signal<boolean>(true);

/** Separate toggle for the smoke particle pool (perf-conscious). */
export const explosionSmokeEnabledSignal = signal<boolean>(true);

let initialised = false;

/**
 * Hydrate both signals from IDB. Called once during boot. Returns
 * count of fields hydrated (0..2) for dev-mode logging.
 */
export async function initExplosionPrefs(): Promise<number> {
  if (initialised) return 0;
  initialised = true;
  let hydrated = 0;
  try {
    const settings = await getSettings();
    const explosion = settings?.explosion;
    if (explosion) {
      if (typeof explosion.enabled === "boolean") {
        explosionEnabledSignal.value = explosion.enabled;
        hydrated++;
      }
      if (typeof explosion.smokeEnabled === "boolean") {
        explosionSmokeEnabledSignal.value = explosion.smokeEnabled;
        hydrated++;
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA explosion-prefs] hydrate failed:", err);
    }
  }
  return hydrated;
}

function persist(): void {
  void mergeSettings({
    explosion: {
      enabled: explosionEnabledSignal.value,
      smokeEnabled: explosionSmokeEnabledSignal.value,
    },
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA explosion-prefs] persist failed:", err);
    }
  });
}

/** Toggle explosion animations master switch (persists immediately). */
export function setExplosionEnabled(enabled: boolean): void {
  explosionEnabledSignal.value = enabled;
  persist();
}

/** Toggle smoke particles separately (persists immediately). */
export function setExplosionSmokeEnabled(enabled: boolean): void {
  explosionSmokeEnabledSignal.value = enabled;
  persist();
}
